import type { AnchorHTMLAttributes, MouseEvent, MouseEventHandler } from "react";
import { Link as RouterLink } from "react-router-dom";
import { WithChildren } from "../../types";
import styles from "./Link.module.css";

type LinkProps = WithChildren & AnchorHTMLAttributes<HTMLAnchorElement> & { disabled?: boolean; };

const isExternalHref = (href: string): boolean => /^(?:[a-z][a-z\d+.-]*:|\/\/)/i.test(href) || href.startsWith("#");

const shouldUseRouterLink = (href?: string, target?: string, download?: AnchorHTMLAttributes<HTMLAnchorElement>["download"]): href is string =>
  !!href && target !== "_blank" && !download && !isExternalHref(href);

const getClassName = (className?: string, invisible = false) =>
  [styles.link, invisible && styles.invisible, className].filter(Boolean).join(" ");

const handleDisabledClick = (event: MouseEvent<HTMLAnchorElement>, onClick?: MouseEventHandler<HTMLAnchorElement>) => {
  onClick?.(event);
  event.preventDefault();
  event.stopPropagation();
};

const BaseLink = ({ children, className, href, onClick, disabled = false, target, download, ...props }: LinkProps & { invisible?: boolean }) => {
  const computedClassName = getClassName(className, props.invisible);

  if (shouldUseRouterLink(href, target, download)) {
    return (
      <RouterLink
        aria-disabled={disabled || undefined}
        className={computedClassName}
        onClick={disabled ? (event) => handleDisabledClick(event, onClick) : onClick}
        tabIndex={disabled ? -1 : props.tabIndex}
        to={href}
        {...props}
      >
        {children}
      </RouterLink>
    );
  }

  return (
    <a
      aria-disabled={disabled || undefined}
      className={computedClassName}
      download={download}
      href={href}
      onClick={disabled ? (event) => handleDisabledClick(event, onClick) : onClick}
      tabIndex={disabled ? -1 : props.tabIndex}
      target={target}
      {...props}
    >
      {children}
    </a>
  );
};

const LinkContainer = ({ children, ...props }: LinkProps) => <BaseLink {...props}>{children}</BaseLink>;

export const InvisibleLink = ({ children, ...props }: LinkProps) => (
  <BaseLink invisible {...props}>
    {children}
  </BaseLink>
);

export default LinkContainer;
